"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getConvertedProductModifierPricingStructure = exports.getConvertedModifierPricingStructure = exports.getModifierPrice = void 0;
const core_1 = require("@natomas-org/core");
const selections_1 = require("../selections");
const getModifierPrice = (modifier, selections) => {
    const priceBeforeAdjustments = getModifierPriceBeforeAdjustments(modifier, selections);
    const priceAdjustments = getModifierPriceAdjustments(modifier, selections);
    return core_1.Price.sumPrices(priceBeforeAdjustments, priceAdjustments);
};
exports.getModifierPrice = getModifierPrice;
const getModifierPriceBeforeAdjustments = (modifier, selections) => {
    // No price set exists
    if (!modifier.price_set) {
        const legacyPrice = getLegacyPricing(modifier, selections);
        return { price: legacyPrice, cost: legacyPrice, currency: "USD" };
    }
    const baselinePrices = modifier.price_set.filter((price) => price.type === core_1.ModifierPriceType.BASELINE ||
        price.condition === core_1.ModifierPriceCondition.DEFAULT);
    let defaultPrice = null;
    for (const price of baselinePrices) {
        if (price.condition === core_1.ModifierPriceCondition.DEFAULT) {
            // Find the default baseline price, in case no other conditional prices match
            defaultPrice = price.price;
        }
        else {
            // Otherwise, hunt down any satisfied conditional prices
            const isValidPrice = isPriceConditionMet(price, selections);
            if (isValidPrice) {
                return price.price;
            }
        }
    }
    return defaultPrice !== null && defaultPrice !== void 0 ? defaultPrice : { price: 0, cost: 0, currency: "USD" };
};
const getLegacyPricing = (modifier, selections) => {
    if (modifier.prices != null) {
        for (const price of modifier.prices) {
            let isValidPrice = true;
            if (price.condition === "any") {
                isValidPrice = Object.keys(price.requiredModifiers).some((modifierId) => {
                    // Update to isSelected + Valid
                    return (0, selections_1.isModifierSelected)(modifierId, selections);
                });
            }
            else {
                isValidPrice = Object.keys(price.requiredModifiers).every((modifierId) => {
                    // Update to isSelected + Valid
                    return (0, selections_1.isModifierSelected)(modifierId, selections);
                });
            }
            if (isValidPrice) {
                return getPriceMicros(price.priceMicros);
            }
        }
    }
    if (shouldPriceBeUsed(modifier.priceMicros)) {
        return getPriceMicros(modifier.priceMicros);
    }
    if (modifier.defaultPrices != null) {
        for (const price of modifier.defaultPrices) {
            let isValidPrice = true;
            Object.keys(price.requiredModifiers).forEach((modifierId) => {
                isValidPrice =
                    isValidPrice && (0, selections_1.isModifierSelected)(modifierId, selections);
            });
            if (isValidPrice) {
                return getPriceMicros(price.priceMicros);
            }
        }
    }
    return getPriceMicros(modifier.defaultPriceMicros);
};
const getPriceMicros = (value) => {
    return value == null ? 0 : value;
};
const shouldPriceBeUsed = (value) => {
    return value != null && (value >= 1 || value <= -1);
};
const isPriceConditionMet = (price, selections) => {
    let isValidPrice = false;
    if (price.condition === core_1.ModifierPriceCondition.ANY) {
        isValidPrice = Object.keys(price.dependentModifiers).some((modifierId) => {
            return (0, selections_1.isModifierSelected)(modifierId, selections);
        });
    }
    else if (price.condition === core_1.ModifierPriceCondition.ALL) {
        isValidPrice = Object.keys(price.dependentModifiers).every((modifierId) => {
            return (0, selections_1.isModifierSelected)(modifierId, selections);
        });
    }
    return isValidPrice;
};
const getModifierPriceAdjustments = (modifier, selections) => {
    let adjustedPriceMicros = 0;
    let adjustedCostMicros = 0;
    const adjustmentPrices = modifier.price_set.filter((price) => price.type === core_1.ModifierPriceType.ADJUSTMENT);
    for (const price of adjustmentPrices) {
        const isValidPrice = isPriceConditionMet(price, selections);
        if (isValidPrice) {
            adjustedPriceMicros += getPriceMicros(price === null || price === void 0 ? void 0 : price.price.price);
            adjustedCostMicros += getPriceMicros(price === null || price === void 0 ? void 0 : price.price.cost);
        }
    }
    return {
        cost: adjustedCostMicros,
        price: adjustedPriceMicros,
        currency: "USD",
    };
};
const getConvertedModifierPricingStructure = (modifier) => {
    var _a, _b, _c;
    const prices = [];
    const defaultPrice = {
        price: {
            cost: null,
            price: (_a = modifier === null || modifier === void 0 ? void 0 : modifier.defaultPriceMicros) !== null && _a !== void 0 ? _a : null,
            currency: "USD",
        },
        condition: core_1.ModifierPriceCondition.DEFAULT,
        dependentModifiers: [],
        type: core_1.ModifierPriceType.BASELINE,
    };
    prices.push(defaultPrice);
    if (modifier === null || modifier === void 0 ? void 0 : modifier.defaultPriceAdjustments) {
        (_b = modifier === null || modifier === void 0 ? void 0 : modifier.defaultPriceAdjustments) === null || _b === void 0 ? void 0 : _b.forEach((originalPriceAdjustment) => {
            var _a, _b;
            const reformattedPriceAdjustment = {
                price: {
                    cost: null,
                    price: (_a = originalPriceAdjustment === null || originalPriceAdjustment === void 0 ? void 0 : originalPriceAdjustment.priceMicros) !== null && _a !== void 0 ? _a : null,
                    currency: "USD",
                },
                condition: originalPriceAdjustment === null || originalPriceAdjustment === void 0 ? void 0 : originalPriceAdjustment.condition,
                dependentModifiers: (_b = originalPriceAdjustment === null || originalPriceAdjustment === void 0 ? void 0 : originalPriceAdjustment.requiredModifiers) !== null && _b !== void 0 ? _b : [],
                type: core_1.ModifierPriceType.ADJUSTMENT,
            };
            prices.push(reformattedPriceAdjustment);
        });
    }
    if (modifier === null || modifier === void 0 ? void 0 : modifier.defaultPrices) {
        (_c = modifier === null || modifier === void 0 ? void 0 : modifier.defaultPrices) === null || _c === void 0 ? void 0 : _c.forEach((originalPriceDependency) => {
            var _a, _b;
            const reformattedPriceDependency = {
                price: {
                    cost: null,
                    price: (_a = originalPriceDependency === null || originalPriceDependency === void 0 ? void 0 : originalPriceDependency.priceMicros) !== null && _a !== void 0 ? _a : null,
                    currency: "USD",
                },
                condition: originalPriceDependency === null || originalPriceDependency === void 0 ? void 0 : originalPriceDependency.condition,
                dependentModifiers: (_b = originalPriceDependency === null || originalPriceDependency === void 0 ? void 0 : originalPriceDependency.requiredModifiers) !== null && _b !== void 0 ? _b : [],
                type: core_1.ModifierPriceType.BASELINE,
            };
            prices.push(reformattedPriceDependency);
        });
    }
    return prices;
};
exports.getConvertedModifierPricingStructure = getConvertedModifierPricingStructure;
const getConvertedProductModifierPricingStructure = (modifier) => {
    var _a, _b, _c;
    const prices = [];
    const defaultPrice = {
        price: {
            cost: null,
            price: (_a = modifier === null || modifier === void 0 ? void 0 : modifier.priceMicros) !== null && _a !== void 0 ? _a : null,
            currency: "USD",
        },
        condition: core_1.ModifierPriceCondition.DEFAULT,
        dependentModifiers: [],
        type: core_1.ModifierPriceType.BASELINE,
    };
    prices.push(defaultPrice);
    if (modifier === null || modifier === void 0 ? void 0 : modifier.priceAdjustments) {
        (_b = modifier === null || modifier === void 0 ? void 0 : modifier.priceAdjustments) === null || _b === void 0 ? void 0 : _b.forEach((originalPriceAdjustment) => {
            var _a, _b;
            const reformattedPriceAdjustment = {
                price: {
                    cost: null,
                    price: (_a = originalPriceAdjustment === null || originalPriceAdjustment === void 0 ? void 0 : originalPriceAdjustment.priceMicros) !== null && _a !== void 0 ? _a : null,
                    currency: "USD",
                },
                condition: originalPriceAdjustment === null || originalPriceAdjustment === void 0 ? void 0 : originalPriceAdjustment.condition,
                dependentModifiers: (_b = originalPriceAdjustment === null || originalPriceAdjustment === void 0 ? void 0 : originalPriceAdjustment.requiredModifiers) !== null && _b !== void 0 ? _b : [],
                type: core_1.ModifierPriceType.ADJUSTMENT,
            };
            prices.push(reformattedPriceAdjustment);
        });
    }
    if (modifier === null || modifier === void 0 ? void 0 : modifier.prices) {
        (_c = modifier === null || modifier === void 0 ? void 0 : modifier.prices) === null || _c === void 0 ? void 0 : _c.forEach((originalPriceDependency) => {
            var _a, _b;
            const reformattedPriceDependency = {
                price: {
                    cost: null,
                    price: (_a = originalPriceDependency === null || originalPriceDependency === void 0 ? void 0 : originalPriceDependency.priceMicros) !== null && _a !== void 0 ? _a : null,
                    currency: "USD",
                },
                condition: originalPriceDependency === null || originalPriceDependency === void 0 ? void 0 : originalPriceDependency.condition,
                dependentModifiers: (_b = originalPriceDependency === null || originalPriceDependency === void 0 ? void 0 : originalPriceDependency.requiredModifiers) !== null && _b !== void 0 ? _b : [],
                type: core_1.ModifierPriceType.BASELINE,
            };
            prices.push(reformattedPriceDependency);
        });
    }
    return prices;
};
exports.getConvertedProductModifierPricingStructure = getConvertedProductModifierPricingStructure;
//# sourceMappingURL=modifier.js.map